import { Button, Modal, message, Space, List, Alert } from 'antd';
import { MinusCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { useState, useReducer, useRef } from 'react';
import { uniqBy } from 'lodash';
import { request } from '@/utils/request';

export default function UploadFile({ uploadCallback }: { uploadCallback: Function }) {
  const [visible, setVisible] = useState<boolean>(false);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileList, dispatchFiles] = useReducer(filesReducer, []);
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadResultMap, setUploadResultMap] = useState<{ [key: string]: { code: number; msg: string; } }>({});

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  function clear() {
    setVisible(false);
    setUploadResultMap({});
    dispatchFiles({ type: 'init', data: [] });
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const files = e.dataTransfer.files;
    handleFiles(files);
  };

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    dispatchFiles({ type: 'add', data: Array.from(files).filter(f => f.size <= 1024 * 1024 * 512) });
    inputRef.current!.value = '';
  };

  const uploadFile = async () => {
    if (fileList.length === 0) return message.warning('请选择上传的文件！');

    setLoading(true);
    const res = await Promise
      .all(fileList.map(file => {
        const formData = new FormData();
        formData.append('file', file, file.name);
        return request('/local/knowledge/files/upload', {
          method: 'POST',
          data: formData
        })
      }));
    setLoading(false);
    let uploadResultMap: { [key: string]: { msg: string; code: number; } } = {};
    let successCount = 0;
    res.forEach((item, index) => {
      const resp = item?.resp;
      const error = item?.error;
      if (resp?.code === 0) {
        uploadResultMap[fileList[index].name as string] = {
          msg: resp.message!,
          code: 0,
        };
        successCount++;
      } else {
        const message = error?.message || "";
        const messageSplit = message.split('上传失败！');
        uploadResultMap[fileList[index].name as string] = {
          msg: messageSplit.length !== 0 ? messageSplit[messageSplit.length - 1] : "",
          code: resp?.code as number
        };
      }
    })
    setUploadResultMap(uploadResultMap);
    if (successCount > 0) {
      uploadCallback();
    }
    message.success(`成功上传${successCount}个文件`);
  };

  const dropAreaStyle = {
    width: '100%',
    height: '200px',
    border: `2px dashed ${dragging ? 'blue' : '#ccc'}`,
    textAlign: 'center',
    padding: '20px',
    cursor: 'pointer',
    margin: '0 auto',
    position: 'relative',
  } as React.CSSProperties;

  const inputStyle = { position: 'absolute', left: 0, top: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer' } as React.CSSProperties;

  const listStyle = { maxHeight: 166, overflowY: 'auto' } as React.CSSProperties;

  return <>
    <Button type="primary" onClick={() => setVisible(true)}><UploadOutlined />上传文件</Button>
    <Modal
      visible={visible}
      title="上传文件"
      width={700}
      onCancel={clear}
      onOk={uploadFile}
      footer={<Space>
        <Button key="back" onClick={clear}>取消</Button>
        <Button key="submit" type="primary" loading={loading} onClick={uploadFile}>确定上传</Button>
      </Space>}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <div
          id="drop-area"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={dropAreaStyle}
        >
          <p>点击选择或拖放文件</p>
          <p>支持文件类型：c, cpp, csv, docx, html, java, json, md, pdf, php, pptx, py, rb, tex, txt, css, js, tar, ts, xlsx, xml, zip</p>
          <p>文件大小：最大支持512MB或200万token</p>
          <input
            type="file"
            id="file-input"
            ref={inputRef}
            multiple
            accept=".c, .cpp, .csv, .docx, .html, .java, .json, .md, .pdf, .php, .pptx, .py, .rb, .tex, .txt, .css, .js, .tar, .ts, .xlsx, .xml, .zip"
            onChange={(e) => handleFiles(e.target.files)}
            style={inputStyle}
          />
        </div>
        <div style={listStyle}>
          <List
            locale={{ emptyText: '暂无文件' }}
            dataSource={fileList}
            renderItem={f => (
              <List.Item
                key={f.name}
                actions={[<a onClick={() => dispatchFiles({ type: 'delete', data: f.name })}><MinusCircleOutlined /></a>]}
              >
                <Space style={{ width: '100%' }} direction="vertical">
                  <span>{f.name}</span>
                  {uploadResultMap[f.name] && <Alert message={`上传结果：${uploadResultMap[f.name].msg}`} type={uploadResultMap[f.name].code === 0 ? 'success' : 'error'} />}
                </Space>
              </List.Item>
            )}
          />
        </div>
        {fileList.length > 0 && <div style={{ display: 'flex', justifyContent: 'flex-end' }}>共计{fileList.length}个文件</div>}
      </Space>
    </Modal>
  </>
}

function filesReducer(state: File[], action: { type: 'add' | 'init' | 'delete', data: any }): File[] {
  switch (action.type) {
    case 'init': {
      return [];
    }
    case 'add': {
      return uniqBy([...state, ...(action.data as File[])], 'name')
    }
    case 'delete': {
      return state.filter(f => f.name !== action.data)
    }
    default: {
      throw "type类型不存在！"
    }
  }
}

